/**
 * Messages the user sends while Clu is still answering wait here, one queue
 * per chat, until they go out together as one message. Pure functions over an
 * immutable map; the runtime (useAgentChatRuntime) owns the state.
 */
import { isPendingKey, pendingKey } from '@/hooks/local-chats.util';
import type { AgentMessage } from '@/hooks/use-agent-chat.hook';

/** The chat on screen before it has an id. */
export const NEW_CHAT = 'new-chat';

export type QueuedMessage = { id: string; text: string };

/**
 * A chat's queue. `held` means it waits for the user: the turn it waited on
 * was stopped, failed or ended on a confirmation card, so it never goes out
 * on its own again. `untilCard` marks a hold that only a confirmation card
 * put there: the user answering the card (Cancel or Confirm) releases it, so
 * it goes out at the end of that answer's turn.
 */
export type ChatQueue = {
  items: readonly QueuedMessage[];
  held: boolean;
  untilCard?: boolean;
};

/**
 * Keyed by conversation id, NEW_CHAT, or the key of a new chat left before
 * its id came (`pending:<clientTurnId>`, see startedChatKey). An empty queue
 * has no key.
 */
export type MessageQueues = Readonly<Record<string, ChatQueue>>;

export const NO_QUEUES: MessageQueues = Object.freeze({});

export const queueKey = (conversationId: string | null): string =>
  conversationId ?? NEW_CHAT;

/** The messages as one, a blank line between each; empty parts are skipped. */
export const joinMessages = (
  texts: ReadonlyArray<string | null | undefined>
): string =>
  texts
    .map((text) => text?.trim() ?? '')
    .filter(Boolean)
    .join('\n\n');

const LIST_MENTION_PATTERN = /@\[([^\]]+)\]\(([^)]+)\)/g;

/** A queued message on one line, a list mention as `@label`. */
export const queuedMessageLabel = (text: string): string =>
  text.replace(LIST_MENTION_PATTERN, '@$1').replace(/\s+/g, ' ').trim();

const without = (queues: MessageQueues, key: string): MessageQueues => {
  if (!(key in queues)) return queues;
  const next = { ...queues };
  delete next[key];
  return next;
};

const withItems = (
  queues: MessageQueues,
  key: string,
  items: readonly QueuedMessage[],
  held: boolean,
  untilCard = false
): MessageQueues =>
  items.length === 0
    ? without(queues, key)
    : {
        ...queues,
        [key]: held && untilCard ? { items, held, untilCard } : { items, held },
      };

/** Adds a message at the end of the chat's queue, held or not as it was. */
export function enqueue(
  queues: MessageQueues,
  key: string,
  message: QueuedMessage
): MessageQueues {
  const queue = queues[key];
  return withItems(
    queues,
    key,
    [...(queue?.items ?? []), message],
    queue?.held ?? false,
    queue?.untilCard
  );
}

/** Drops one message; the rest keep their order. */
export function removeQueued(
  queues: MessageQueues,
  key: string,
  id: string
): MessageQueues {
  const queue = queues[key];
  if (!queue) return queues;
  const items = queue.items.filter((item) => item.id !== id);
  if (items.length === queue.items.length) return queues;
  return withItems(queues, key, items, queue.held, queue.untilCard);
}

/** Takes one message out of the queue, for the composer. */
export function takeQueued(
  queues: MessageQueues,
  key: string,
  id: string
): [MessageQueues, QueuedMessage | null] {
  const item = queues[key]?.items.find((queued) => queued.id === id) ?? null;
  if (!item) return [queues, null];
  return [removeQueued(queues, key, id), item];
}

/** Takes the whole queue as one message; null when it is empty. */
export function takeAll(
  queues: MessageQueues,
  key: string
): [MessageQueues, string | null] {
  const queue = queues[key];
  if (!queue) return [queues, null];
  const text = joinMessages(queue.items.map((item) => item.text));
  return [without(queues, key), text || null];
}

/**
 * Marks the chat's queue held until the user sends it. An empty queue stays
 * empty, not held. A hold a card put there becomes this one.
 */
export function holdQueue(queues: MessageQueues, key: string): MessageQueues {
  const queue = queues[key];
  if (!queue || (queue.held && !queue.untilCard)) return queues;
  return { ...queues, [key]: { items: queue.items, held: true } };
}

/**
 * Marks the chat's queue held while a confirmation card waits: answering the
 * card releases it (releaseCardHold). A queue already held keeps its hold.
 */
export function holdForCard(queues: MessageQueues, key: string): MessageQueues {
  const queue = queues[key];
  if (!queue || queue.held) return queues;
  return {
    ...queues,
    [key]: { items: queue.items, held: true, untilCard: true },
  };
}

/**
 * The card was answered: a queue only the card held goes out at the end of
 * the answer's turn. Any other hold stays for the user.
 */
export function releaseCardHold(
  queues: MessageQueues,
  key: string
): MessageQueues {
  const queue = queues[key];
  if (!queue?.held || !queue.untilCard) return queues;
  return { ...queues, [key]: { items: queue.items, held: false } };
}

/**
 * The messages a waiting card held, taken out: the user's words while the
 * card waited, which a Cancel carries to Clu as its reason. Any other hold
 * keeps its messages.
 */
export function takeCardHold(
  queues: MessageQueues,
  key: string
): [MessageQueues, readonly QueuedMessage[]] {
  const queue = queues[key];
  if (!queue?.held || !queue.untilCard) return [queues, []];
  return [without(queues, key), queue.items];
}

/**
 * Puts messages taken from a card's hold back first, when the Cancel that
 * carried them did not go through. They wait for the card again, or for the
 * user when the card is gone.
 */
export function returnCardHold(
  queues: MessageQueues,
  key: string,
  items: readonly QueuedMessage[],
  untilCard: boolean
): MessageQueues {
  if (items.length === 0) return queues;
  const queue = queues[key];
  return withItems(
    queues,
    key,
    [...items, ...(queue?.items ?? [])],
    true,
    untilCard && (!queue?.held || Boolean(queue.untilCard))
  );
}

/**
 * Moves a queue to another key, after anything already there: a new chat's
 * queue follows the chat once its id is known. Held when either was.
 */
export function moveQueue(
  queues: MessageQueues,
  from: string,
  to: string
): MessageQueues {
  const source = queues[from];
  if (!source || from === to) return queues;
  const target = queues[to];
  const held = source.held || (target?.held ?? false);
  // Still a card's hold only when every hold in it is one.
  const untilCard =
    held &&
    (!source.held || Boolean(source.untilCard)) &&
    (!target?.held || Boolean(target.untilCard));
  return withItems(
    without(queues, from),
    to,
    [...(target?.items ?? []), ...source.items],
    held,
    untilCard
  );
}

type TurnMessage = Pick<
  AgentMessage,
  'role' | 'clientTurnId' | 'content' | 'thinking' | 'confirmationRequest'
> & { toolResults?: readonly unknown[] };

/**
 * A new chat left before the stream named it (the portal's wire names a new
 * chat only when its first turn ends) keeps its queue under the key the
 * sidebar draws it under: its latest turn's. The server saves the user's
 * message before Clu answers, and every message read back carries its turn,
 * so the queue finds the chat again when it opens. Null until Clu has
 * answered that turn, since before that the chat may not exist.
 */
export function startedChatKey(
  messages: readonly TurnMessage[]
): string | null {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message.role !== 'user') continue;
    if (!message.clientTurnId) return null;
    const answered = messages
      .slice(index + 1)
      .some(
        (reply) =>
          reply.role === 'assistant' &&
          (Boolean(reply.content) ||
            typeof reply.thinking === 'string' ||
            Boolean(reply.toolResults?.length) ||
            Boolean(reply.confirmationRequest))
      );
    return answered ? pendingKey(message.clientTurnId) : null;
  }
  return null;
}

/** The queues left by a new chat whose turns these messages hold. */
export function queueKeysOfChat(
  queues: MessageQueues,
  messages: readonly Pick<TurnMessage, 'clientTurnId'>[]
): string[] {
  const keys = Object.keys(queues).filter(isPendingKey);
  if (keys.length === 0) return [];
  const turns = new Set(
    messages.flatMap((message) =>
      message.clientTurnId ? [pendingKey(message.clientTurnId)] : []
    )
  );
  return keys.filter((key) => turns.has(key));
}
